import { useState } from 'react';
import { ChevronDown, Printer } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/DropdownMenu';
import { toast } from '@/hooks/useToast';
import { printBill, rememberedPaper, type BillPaper } from './api';

const LABEL: Record<BillPaper, string> = { a4: 'A4 invoice', receipt: 'Receipt (80 mm)' };

/**
 * 🖨 Print beside a bill's PDF button: sends the invoice straight to the
 * printer (no new tab). The arrow picks A4 or 80 mm receipt paper; the choice
 * is remembered on this computer. Disabled while a job is being prepared, so a
 * double click prints once.
 */
export function PrintBillButton({ billId, size = 'sm', variant = 'ghost', className }: {
  billId: number;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [paper, setPaper] = useState<BillPaper>(rememberedPaper);

  const run = async (p: BillPaper) => {
    if (busy) return;
    setPaper(p);
    setBusy(true);
    try {
      const r = await printBill(billId, p);
      if (r?.fallback) toast({ title: 'Opened in a new tab', description: 'This browser could not print directly — print from the new tab.' });
    } catch (e) {
      toast({ title: 'Could not print the bill', description: e instanceof Error ? e.message : String(e), variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={`inline-flex items-center ${className ?? ''}`}>
      <Button size={size} variant={variant} disabled={busy} onClick={() => run(paper)} title={`Print ${LABEL[paper]}`} className="rounded-r-none">
        <Printer className="h-3.5 w-3.5" /> {busy ? 'Printing…' : 'Print'}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={size} variant={variant} disabled={busy} aria-label="Choose paper size" className="rounded-l-none px-1.5">
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {(Object.keys(LABEL) as BillPaper[]).map((p) => (
            <DropdownMenuItem key={p} onSelect={() => run(p)}>
              {p === paper ? '✓ ' : ''}{LABEL[p]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
